import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@televet/kibble-ui/Button';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { useCallback, useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toaster } from '@/components/ui/toaster';
import { RowActionButton } from '@/components/RowActionButton';
import { EnrollClinicDialog } from './-components/EnrollClinicDialog';
import { InstanceFilters } from './-components/InstanceFilters';
import { instanceFiltersSchema, type InstanceFilterValues } from './-lib/instanceFilters';
import { InstanceStatusTag } from './-components/StatusTag';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';

export const Route = createFileRoute('/_authenticated/emitters/')({
  component: EmitterInstances,
  validateSearch: instanceFiltersSchema,
});

const PAGE_SIZE = 50;

function EmitterInstances() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeNavigate = Route.useNavigate();
  const filters = Route.useSearch();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'pause' | 'resume' | 'archive' | 'trigger';
    instanceId: string;
    instanceName: string;
  } | null>(null);

  // Cursor-based pagination. Cursors stay in component state rather than the
  // URL: one is only valid for the filters it was issued under.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack[cursorStack.length - 1] as string | undefined;

  // Any change to the URL filters starts again at page 1. Reset during render
  // so the stale cursor never reaches a query.
  const filtersKey = JSON.stringify(filters);
  const [pagedFiltersKey, setPagedFiltersKey] = useState(filtersKey);
  if (pagedFiltersKey !== filtersKey) {
    setPagedFiltersKey(filtersKey);
    setCursorStack([]);
  }

  const {
    data: instancesData,
    isLoading,
    error,
    isPlaceholderData,
  } = useQuery(
    trpc.emitter.listInstances.queryOptions(
      useMemo(
        () => ({
          limit: PAGE_SIZE,
          cursor: currentCursor,
          ...(filters.status?.length && { status: filters.status }),
          ...(filters.templateId?.length && { templateId: filters.templateId }),
          ...(filters.ownerType?.length && { ownerType: filters.ownerType }),
          ...(filters.scheduleType?.length && { scheduleType: filters.scheduleType }),
          ...(filters.search && { search: filters.search }),
        }),
        [currentCursor, filters]
      ),
      { placeholderData: keepPreviousData }
    )
  );

  const pageNumber = cursorStack.length + 1;

  // While the previous page stands in, its nextCursor belongs to that page, not this one.
  const nextCursor = isPlaceholderData ? undefined : instancesData?.nextCursor;

  const goNextPage = useCallback(() => {
    if (nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
  }, [nextCursor]);

  const goPrevPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  // Template names for the Template column of the filter dropdown.
  const { data: templatesData } = useQuery(trpc.emitter.listTemplates.queryOptions());

  const [searchDraft, setSearchDraft] = useDebouncedFilterText({
    value: filters.search,
    onCommit: (next) =>
      routeNavigate({ replace: true, search: (prev) => ({ ...prev, search: next }) }),
  });

  const applyFilters = useCallback(
    (next: InstanceFilterValues) => {
      routeNavigate({ search: next });
    },
    [routeNavigate]
  );

  useListingFilters({
    page: 'emitter-instances',
    schema: instanceFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });
  const instances = instancesData?.items;

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [['emitter', 'listInstances']],
      }),
    [queryClient]
  );

  const { mutate: pauseInstance, isPending: isPausing } = useMutation(
    trpc.emitter.pauseInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance paused' });
        setConfirmAction(null);
        invalidate();
      },
      onError: (err) => {
        toaster.error({ title: 'Failed to pause', description: err.message });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: resumeInstance, isPending: isResuming } = useMutation(
    trpc.emitter.resumeInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance resumed' });
        setConfirmAction(null);
        invalidate();
      },
      onError: (err) => {
        toaster.error({ title: 'Failed to resume', description: err.message });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: archiveInstance, isPending: isArchiving } = useMutation(
    trpc.emitter.archiveInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance archived' });
        setConfirmAction(null);
        invalidate();
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to archive',
          description: err.message,
        });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: triggerExecution, isPending: isTriggering } = useMutation(
    // @ts-expect-error TS2589: tRPC + TanStack Router type inference depth
    trpc.emitter.triggerExecution.mutationOptions({
      onSuccess: (_data, variables) => {
        toaster.success({ title: 'Execution triggered' });
        setConfirmAction(null);
        invalidate();
        const inst = instances?.find((i) => i.id === variables.instanceId);
        if (inst) {
          navigate({
            to: `/emitter-templates/${inst.template.id}/instances/${inst.id}/executions` as string,
          });
        }
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to trigger',
          description: err.message,
        });
        setConfirmAction(null);
      },
    })
  );

  const { mutate: createInstance, isPending: isCreating } = useMutation(
    trpc.emitter.createInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Enrolled successfully' });
        setEnrollOpen(false);
        invalidate();
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to enroll',
          description: err.message,
        });
      },
    })
  );

  const handleConfirm = () => {
    if (!confirmAction) return;
    switch (confirmAction.type) {
      case 'pause':
        pauseInstance({ id: confirmAction.instanceId });
        break;
      case 'resume':
        resumeInstance({ id: confirmAction.instanceId });
        break;
      case 'archive':
        archiveInstance({ id: confirmAction.instanceId });
        break;
      case 'trigger':
        triggerExecution({ instanceId: confirmAction.instanceId });
        break;
    }
  };

  const confirmLabels: Record<
    string,
    { title: string; description: string; label: string; color?: string }
  > = {
    pause: {
      title: 'Pause Instance',
      description: `Pause "${confirmAction?.instanceName}"? Scheduled executions will be skipped while paused.`,
      label: 'Pause',
      color: 'yellow',
    },
    resume: {
      title: 'Resume Instance',
      description: `Resume "${confirmAction?.instanceName}"? Scheduled executions will begin running again.`,
      label: 'Resume',
      color: 'green',
    },
    archive: {
      title: 'Archive Instance',
      description: `Archive "${confirmAction?.instanceName}"? This instance will no longer execute and cannot be resumed.`,
      label: 'Archive',
      color: 'red',
    },
    trigger: {
      title: 'Trigger Execution',
      description: `Trigger a Full execution for "${confirmAction?.instanceName}"? This will persist results and emit events.`,
      label: 'Trigger',
      color: 'blue',
    },
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs items={[{ label: 'Instances' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Instances
          </Text>
          <Button size="sm" iconName="plus" onClick={() => setEnrollOpen(true)}>
            Enroll
          </Button>
        </Flex>

        <InstanceFilters
          values={filters}
          onChange={applyFilters}
          templates={templatesData ?? []}
          searchDraft={searchDraft}
          onSearchDraftChange={setSearchDraft}
        />

        {isLoading && (
          <Flex justify="center" py={8}>
            <Spinner size="lg" centered={false} />
          </Flex>
        )}

        {error && (
          <Flex justify="center" py={6}>
            <Text fontSize="sm" variant="danger">
              Failed to load instances: {error.message}
            </Text>
          </Flex>
        )}

        {!isLoading && !error && (
          <Table.Root size="sm" width="100%" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Template</Table.ColumnHeader>
                <Table.ColumnHeader>Owner</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Version</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Schedule</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Status</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="center">Runs</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {instances?.map((instance) => (
                <Table.Row
                  key={instance.id}
                  cursor="pointer"
                  _hover={{ bg: 'background.subtle' }}
                  onClick={() =>
                    navigate({
                      to: '/emitters/$instanceId',
                      params: { instanceId: instance.id },
                    })
                  }
                >
                  <Table.Cell>
                    <Text fontSize="sm" fontWeight="medium">
                      {instance.name ?? '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" variant="subtle">
                      {instance.template.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" variant="subtle" fontFamily="mono" truncated maxW="220px">
                      {instance.ownerType}:{instance.ownerId}
                      {instance.ownerName ? ` (${instance.ownerName})` : ''}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Text fontSize="xs" variant="subtle">
                      v{instance.templateVersion.versionNumber}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Tag
                      label={instance.configuration?.scheduleType ?? '-'}
                      variant="default"
                      size="sm"
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <InstanceStatusTag status={instance.status} />
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Text fontSize="xs" variant="subtle">
                      {instance._count.executions}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <Flex gap={0.5} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                      {instance.status === 'Active' && (
                        <RowActionButton
                          label="Pause"
                          iconName="pause"
                          onClick={() =>
                            setConfirmAction({
                              type: 'pause',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`instance-pause-${instance.id}`}
                        />
                      )}
                      {instance.status === 'Paused' && (
                        <RowActionButton
                          label="Resume"
                          iconName="play"
                          onClick={() =>
                            setConfirmAction({
                              type: 'resume',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`instance-resume-${instance.id}`}
                        />
                      )}
                      {instance.status !== 'Archived' && (
                        <RowActionButton
                          label="Archive"
                          iconName="archive"
                          variant="ghostDestructive"
                          onClick={() =>
                            setConfirmAction({
                              type: 'archive',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`instance-archive-${instance.id}`}
                        />
                      )}
                      <RowActionButton
                        label="Edit"
                        iconName="pen"
                        onClick={() =>
                          navigate({
                            to: '/emitters/$instanceId',
                            params: { instanceId: instance.id },
                          })
                        }
                        data-testid={`instance-edit-${instance.id}`}
                      />
                      <RowActionButton
                        label="Executions"
                        iconName="clock"
                        onClick={() =>
                          navigate({
                            to: `/emitter-templates/${instance.template.id}/instances/${instance.id}/executions` as string,
                          })
                        }
                        data-testid={`instance-executions-${instance.id}`}
                      />
                      {/* triggerExecution rejects any instance that is not Active. */}
                      {instance.status === 'Active' && (
                        <RowActionButton
                          label="Run now"
                          iconName="magic"
                          variant="ghost"
                          onClick={() =>
                            setConfirmAction({
                              type: 'trigger',
                              instanceId: instance.id,
                              instanceName: instance.name ?? instance.template.name,
                            })
                          }
                          data-testid={`instance-run-now-${instance.id}`}
                        />
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}

        {!isLoading && !error && instances?.length === 0 && (
          <Flex justify="center" py={6}>
            <Text fontSize="sm" variant="subtle">
              No instances found
            </Text>
          </Flex>
        )}

        {!isLoading && !error && (
          <Flex justify="space-between" align="center">
            <Text fontSize="xs" variant="subtle">
              {instances?.length ?? 0} instances on this page
            </Text>
            <Flex align="center" gap={2}>
              <Text fontSize="sm" variant="subtle">
                Page {pageNumber}
              </Text>
              <Button
                size="xs"
                variant="ghostNeutral"
                iconName="chevronLeft"
                onClick={goPrevPage}
                disabled={cursorStack.length === 0}
                aria-label="Previous page"
              />
              <Button
                size="xs"
                variant="ghostNeutral"
                iconName="chevronRight"
                onClick={goNextPage}
                disabled={!nextCursor}
                aria-label="Next page"
              />
            </Flex>
          </Flex>
        )}
      </Flex>

      <EnrollClinicDialog
        isOpen={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onSubmit={createInstance}
        isSubmitting={isCreating}
      />

      {confirmAction && (
        <ConfirmDialog
          isOpen
          title={confirmLabels[confirmAction.type].title}
          description={confirmLabels[confirmAction.type].description}
          confirmLabel={confirmLabels[confirmAction.type].label}
          variant={
            confirmLabels[confirmAction.type].color === 'red' ? 'primaryDestructive' : 'primary'
          }
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          isLoading={isPausing || isResuming || isArchiving || isTriggering}
        />
      )}
    </PageContainer>
  );
}
